import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { canFallBackOffline } from '@/lib/api/offlineFallback';
import { useSession } from '@/lib/auth/session';
import { closedKeysFor, widgetGateKey } from '@/lib/menu/menuGate';
import { isAdministrator } from '@/lib/types';
import { placeSlots, type PlacedWidget, type WidgetSlot } from '@/pages/home/widgetLayout';
import { widgetByKey, WIDGET_DEFINITIONS, type WidgetDefinition } from '@/pages/home/widgets/registry';

/**
 * 사용자별 홈 위젯 배치.
 *
 * ## 서버가 진실이다
 *
 * 배치는 개인 설정이라 사용자별로 서버에 남긴다(`GET/PUT/DELETE /api/v1/home/widgets`). 그래야 기기를
 * 바꿔도 내가 고른 위젯이 그대로 따라온다. 저장은 배치를 통째로 보낸다 — 화면이 mergeSlots 로 카탈로그와
 * 맞춘 전체 목록을 PUT 하고, 서버는 그 사용자의 행을 전부 갈아끼운다.
 *
 * 개발 중 백엔드에 닿지 못하면(서버 꺼짐) localStorage 로 물러선다 — 세션이 목으로 열리는 것과 같은
 * 조건({@link env.mockSessionWhenOffline})이다. 운영에서는 절대 물러서지 않는다.
 *
 * ## 저장된 것과 카탈로그가 어긋날 때
 *
 * 위젯은 늘어나고 권한은 바뀐다. 그래서 저장된 배치를 그대로 믿지 않고 매번 카탈로그와 맞춘다.
 *  - 카탈로그에 없는 키(위젯이 없어졌거나 권한을 잃음)는 버린다
 *  - 카탈로그에 있는데 저장에 없는 키(새로 생긴 위젯)는 기본값으로 뒤에 붙인다
 *  - 고정 위젯은 저장된 값이 무엇이든 항상 보이고, 항상 앞에 온다
 *
 * ## 고정에는 두 겹이 있다
 *
 * `registry.ts` 의 `fixed` 는 **코드에 박힌 정적 값**이다(개인 인사정보·할일·공지 — 조건 없이 늘 고정).
 * 그 위에 **동적 고정**을 한 겹 얹는다(`GET /v1/home/widgets/conditions` 의 `forced`): 착공 준비 알림처럼
 * 「계약 날인 ~ 착공 사이에만, 그 현장에 관계된 사람에게만」 강제되는 위젯은 조건이 DB 에 있어
 * 화면이 알 수 없다. `fixed` 자체를 서버 값으로 바꾸지 않는 이유는, 바꾸면 조건이 없는 위젯까지
 * 매번 서버에 물어야 하기 때문이다.
 *
 * 강제 목록을 못 받아도(오프라인·오류) 화면은 그대로 뜬다 — 그때는 정적 고정만 적용된다.
 * 강제는 **재촉하는 장치**이지 인가가 아니므로, 못 받았다고 홈을 막을 이유가 없다.
 *
 * 자리는 **정적 고정(할일·공지) 다음**이다(`rankOf`).
 *
 * ## 서버가 카탈로그도 정한다 (2026-08-27 지시)
 *
 * `onlyWhenEligible` 위젯은 서버가 자격을 준 동안에만 `available` 에 들어간다 — 착공 준비(계약
 * 날인 ~ 착공 사이의 현장이 있을 때)와 기술인 교육(기술등급·법정교육 기록이 있을 때). 그래서
 * 해당 없는 사람의 위젯 관리 목록에는 **아예 나오지 않는다.** 목록에 남겨 두면 켜 봐야 늘 빈
 * 칸이고, 그 빈 칸을 며칠 보고 나면 정작 조건이 섰을 때도 읽지 않는다.
 *
 * **자격과 강제는 다르다.** 착공 준비는 자격이 곧 강제라 저절로 켜지고, 기술인 교육은 자격만
 * 주고 켜고 끄는 것은 본인 몫이다(`defaultVisible`) — 계속교육 만료는 3년짜리 기한이라 몇 주짜리
 * 착공 기한과 같은 무게로 재촉할 일이 아니다.
 *
 * 조건이 끝나면 카탈로그에서 빠지고, 저장된 배치의 그 줄도 다음 저장에서 버려진다(`mergeSlots`).
 *
 * ## 강제는 「저절로 켜진다」 이지 「못 끈다」 가 아니다 (2026-08-26 지시)
 *
 * 조건이 서면 위젯이 **알아서 켜지고 그 자리에 선다**. 그런데 이미 알고 처리 중인 사람에게까지
 * 끌 수 없는 알림을 몇 주씩 띄우면, 그 사람은 곧 그 자리를 **보지 않는 법을 배운다** — 그러면
 * 정작 급할 때도 안 보인다. 그래서 위젯 관리에서 닫을 수 있게 두되, **닫았다는 사실을 기억한다**
 * (`dismissedForced`). 조건이 끝나면 그 기억도 지워, 다음 현장에서는 다시 켜진다.
 */

/**
 * 배치 한 줄. 무엇을·어떤 크기로 볼지에 더해 <b>배율 배치</b>(폭·높이 %와 핀)까지 담는다 —
 * 정의는 {@link widgetLayout} 에 있다. 저장·복원 경로가 하나라 여기서 함께 실어 나른다.
 */
export type { WidgetSlot } from '@/pages/home/widgetLayout';

/** `GET /v1/home/widgets/conditions` — 조건이 정하는 것 둘. `forced` 는 `eligible` 의 부분집합이다. */
type WidgetConditions = {
  /** 위젯 관리 목록에 나올 자격이 있는 조건 위젯. */
  eligible: string[];
  /** 그중 저절로 켜지는 것. */
  forced: string[];
};

export type HomeWidgets = {
  /** 이 계정이 쓸 수 있는 위젯 전부. 위젯 관리 모달이 편집하는 대상이다. */
  slots: WidgetSlot[];
  /** 홈에 실제로 그릴 것. 순서대로다. */
  visible: PlacedWidget[];
  /** 지금 조건이 강제하는 위젯 키(끌 수 없다). 위젯 관리 모달이 「고정」 으로 그린다. */
  forced: Set<string>;
  save: (next: WidgetSlot[]) => void;
  /** 기본 배치로 되돌린다. 저장해 둔 것을 지운다. */
  reset: () => void;
};

const WIDGETS_PATH = '/v1/home/widgets';
const CONDITIONS_PATH = '/v1/home/widgets/conditions';
const STORAGE_PREFIX = 'bomi.home.widgets';
/** 「강제인데 내가 닫았다」 를 적어 두는 자리. 배치와 달리 <b>이 기기에만</b> 남는다(§강제는…). */
const DISMISSED_PREFIX = 'bomi.home.widgets.dismissed';

export function useHomeWidgets(): HomeWidgets {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;
  // 관리자는 전 메뉴를 본다. 서버의 visibleMenuKeys 는 비관리자용 목록만 담고 관리자에게는 비어 오므로,
  // 사이드바(`useMenuTree`)가 관리자에게 전 메뉴를 여는 것과 같은 규칙을 위젯 카탈로그에도 적용한다.
  const isAdmin = isAdministrator(session?.user?.userType);
  const visibleMenuKeys = session?.visibleMenuKeys;
  // 아직 열지 않은 것(오픈 게이트 · V153). 위젯은 `widget_` 접두어가 붙은 키로 판정한다.
  const closedKeys = useMemo(() => closedKeysFor(session), [session]);

  const queryClient = useQueryClient();
  const layoutKey = useMemo(() => ['home-widgets', userId] as const, [userId]);

  const { data: stored } = useQuery({
    queryKey: layoutKey,
    queryFn: () => fetchLayout(userId as string),
    enabled: !!userId,
    staleTime: 60_000,
  });

  /*
    조건이 정하는 것 둘 — 카탈로그에 들어올 자격(`eligible`)과 저절로 켜짐(`forced`).
    실패하면 빈 목록으로 두고 화면은 그대로 뜬다(조건은 인가가 아니다). 그때 조건 위젯은
    한 번 사라졌다가 다음 조회에서 돌아온다 — 없는 알림을 지어내는 것보다 낫다.
  */
  const { data: conditions } = useQuery({
    queryKey: ['home-widgets-conditions', userId],
    queryFn: () =>
      apiFetch<WidgetConditions>(CONDITIONS_PATH).catch(() => ({ eligible: [], forced: [] }) as WidgetConditions),
    enabled: !!userId,
    staleTime: 60_000,
  });
  const eligible = useMemo(() => new Set(conditions?.eligible ?? []), [conditions]);
  const forced = useMemo(() => new Set(conditions?.forced ?? []), [conditions]);

  /*
    내가 닫은 강제 위젯. 조건이 끝나면 지운다 — 지우지 않으면 다음 현장이 착공 준비에 들어가도
    그 사람에게만 조용히 안 뜬다. 「이번 건은 알고 있다」 는 뜻이지 「이 알림을 영원히 끈다」 가 아니다.
  */
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!userId) return;
    const kept = new Set([...readDismissed(userId)].filter((key) => forced.has(key)));
    writeDismissed(userId, kept);
    setDismissed(kept);
  }, [forced, userId]);

  /** 지금 실제로 켜 두는 강제 — 닫아 둔 것은 뺀다. */
  const activeForced = useMemo(
    () => new Set([...forced].filter((key) => !dismissed.has(key))),
    [forced, dismissed],
  );

  const available = useMemo(() => {
    const allowed = new Set(visibleMenuKeys ?? []);
    return WIDGET_DEFINITIONS.filter((definition) => {
      // 여럿 적힌 위젯은 「하나라도 있으면」 이다(`registry` 의 `requiresMenuKey` 머리말) —
      // 뉴스·건설동향처럼 한 칸이 자료 둘을 토글로 가르는 경우다.
      const need = definition.requiresMenuKey;
      const menuOk =
        isAdmin || !need || (Array.isArray(need) ? need.some((key) => allowed.has(key)) : allowed.has(need));
      /*
        아직 열지 않은 위젯(오픈 게이트 · V153). 메뉴 권한과 <b>별개의 스위치</b>다 — 게시판은
        열어 두고 홈 첫 화면에만 안 띄우는 경우가 있다(입·퇴사자 소식). 관리자에게는 빈 집합이
        오므로 이 줄을 그냥 지나간다.
      */
      if (closedKeys.has(widgetGateKey(definition.widgetKey))) return false;
      /*
        `onlyWhenEligible` 위젯은 <b>서버가 자격을 준 동안에만</b> 카탈로그에 있다
        (착공 준비 · 기술인 교육). 관리자도 예외가 아니다 — 여기서 거르는 것은 권한이 아니라
        「이 사람에게 내용이 있는가」 라서, 관리자라고 없는 자료가 생기지는 않는다.
      */
      return menuOk && (!definition.onlyWhenEligible || eligible.has(definition.widgetKey));
    });
  }, [closedKeys, eligible, isAdmin, visibleMenuKeys]);

  const slots = useMemo(
    () => mergeSlots(stored ?? null, available, activeForced),
    [stored, available, activeForced],
  );

  const save = useCallback(
    (next: WidgetSlot[]) => {
      if (!userId) return;

      /*
        강제 위젯을 끈 것은 <b>기억해 둔다.</b> 안 그러면 다음 mergeSlots 가 다시 켜서, 사람은
        「껐는데 안 꺼진다」 를 보게 된다. 반대로 다시 켜면 그 기억을 지운다.
      */
      const nextDismissed = new Set(dismissed);
      forced.forEach((key) => {
        const slot = next.find((entry) => entry.widgetKey === key);
        if (!slot) return;
        if (slot.visible) nextDismissed.delete(key);
        else nextDismissed.add(key);
      });
      writeDismissed(userId, nextDismissed);
      setDismissed(nextDismissed);

      const merged = mergeSlots(next, available, new Set([...forced].filter((key) => !nextDismissed.has(key))));
      // 화면은 곧바로 바꾸고(낙관적 갱신), 저장은 뒤에서 한다. 저장이 실패하면 서버 진실로 되돌린다.
      queryClient.setQueryData(layoutKey, merged);
      persistLayout(userId, merged).catch((error) => {
        console.error('[home widgets] 배치 저장 실패', error);
        void queryClient.invalidateQueries({ queryKey: layoutKey });
      });
    },
    [available, dismissed, forced, layoutKey, queryClient, userId],
  );

  const reset = useCallback(() => {
    if (!userId) return;
    // 빈 배치는 "저장한 것이 없음" 이고, mergeSlots 가 여기서 기본값을 만든다.
    queryClient.setQueryData(layoutKey, [] as WidgetSlot[]);
    // 기본 배치로 되돌린다는 것은 「닫아 둔 것」 도 되돌린다는 뜻이다.
    writeDismissed(userId, new Set());
    setDismissed(new Set());
    clearLayout(userId).catch((error) => {
      console.error('[home widgets] 배치 초기화 실패', error);
      void queryClient.invalidateQueries({ queryKey: layoutKey });
    });
  }, [layoutKey, queryClient, userId]);

  const visible = useMemo(() => placeSlots(slots), [slots]);

  return { slots, visible, forced: activeForced, save, reset };
}

function mergeSlots(
  stored: WidgetSlot[] | null,
  available: WidgetDefinition[],
  forced: Set<string> = new Set(),
): WidgetSlot[] {
  const availableKeys = new Set(available.map((definition) => definition.widgetKey));

  const kept = (stored ?? []).filter((slot) => availableKeys.has(slot.widgetKey));
  const keptKeys = new Set(kept.map((slot) => slot.widgetKey));

  const added = available
    .filter((definition) => !keptKeys.has(definition.widgetKey))
    .map((definition) => ({
      widgetKey: definition.widgetKey,
      size: definition.defaultSize,
      // 강제 위젯은 처음 붙을 때부터 켜져 있어야 한다 — 꺼진 채로 붙으면 강제가 무의미하다.
      visible: definition.defaultVisible || forced.has(definition.widgetKey),
    }));

  return [...kept, ...added]
    .map((slot) => {
      const definition = widgetByKey(slot.widgetKey);
      if (!definition) return slot;

      return {
        ...slot,
        widgetKey: slot.widgetKey,
        // 정의에서 사라진 크기가 저장돼 있으면(예: wide 를 없앤 위젯) 기본 크기로 되돌린다.
        size: definition.sizes.includes(slot.size) ? slot.size : definition.defaultSize,
        visible: definition.fixed || forced.has(slot.widgetKey) ? true : slot.visible,
      };
    })
    /*
      고정 위젯이 앞. 같은 무리 안에서는 저장된 순서를 지킨다(정렬이 안정적이다).

      <p>이 순서가 자리를 정하는 것은 <b>배율 배치를 쓰지 않을 때뿐</b>이다. 배치 커스텀을 켠
      사람의 자리는 좌표가 정한다({@link widgetLayout} 의 `resolveLayout`) — 거기서 이 순서는
      「좌표가 아직 없는 위젯을 어느 차례로 빈 자리에 앉힐지」 에만 쓰인다.
    */
    .sort((a, b) => rankOf(a.widgetKey, forced) - rankOf(b.widgetKey, forced));
}

/**
 * 앞에 세울 순서.
 *
 * <pre>
 *   0  늘 고정  — 할일 · 공지 (`fixed`)
 *   1  조건 고정 — 착공 준비처럼 서버가 켠 것
 *   2  나머지
 * </pre>
 *
 * <b>조건 고정을 맨 앞에 두지 않는다</b>(사용자 지시 2026-08-26). 처음에는 「지금 급한 것」 이라
 * 앞세웠는데, 그러면 착공 준비 중인 현장이 하나라도 있는 동안 <b>매일 여는 할일·공지가 아래로
 * 밀린다.</b> 급한 일은 몇 주짜리이고 할일·공지는 매일이라, 밀리는 쪽의 손해가 더 크다.
 * 강제의 뜻은 「끌 수 없다」 이지 「가장 위」 가 아니다 — 끄지 못하게만 두고 자리는 뒤로 준다.
 */
function rankOf(widgetKey: string, forced: Set<string>): number {
  if (widgetByKey(widgetKey)?.fixed) return 0;
  if (forced.has(widgetKey)) return 1;
  return 2;
}

// ── 서버 왕래 (오프라인이면 localStorage 로 물러선다) ────────────────────────────

type StoredSlot = {
  widgetKey: string;
  size: string;
  visible: boolean;
  widthPercent?: number;
  heightPercent?: number;
  col?: number;
  row?: number;
  pinned?: boolean;
};

async function fetchLayout(userId: string): Promise<WidgetSlot[] | null> {
  try {
    const data = await apiFetch<StoredSlot[] | null>(WIDGETS_PATH);
    return Array.isArray(data) ? data.filter(isSlot) : null;
  } catch (error) {
    if (canFallBackOffline(error)) return readLocalLayout(userId);
    throw error;
  }
}

async function persistLayout(userId: string, slots: WidgetSlot[]): Promise<void> {
  try {
    await apiFetch<unknown>(WIDGETS_PATH, { method: 'PUT', body: { slots } });
  } catch (error) {
    if (canFallBackOffline(error)) {
      writeLocalLayout(userId, slots);
      return;
    }
    throw error;
  }
}

async function clearLayout(userId: string): Promise<void> {
  try {
    await apiFetch<unknown>(WIDGETS_PATH, { method: 'DELETE' });
  } catch (error) {
    if (canFallBackOffline(error)) {
      removeLocalLayout(userId);
      return;
    }
    throw error;
  }
}

// ── 오프라인 캐시 (개발용) ──────────────────────────────────────────────────────
//
// 저장 키에 사용자 id 를 넣는 이유: 같은 PC 를 여러 사람이 쓰는 현장 사무실이 있다.
// 키를 하나로 두면 앞사람의 배치가 뒷사람 화면에 그대로 뜬다.

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}.${userId}`;
}

function readLocalLayout(userId: string): WidgetSlot[] | null {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;

    // 손으로 고쳤거나 예전 형식일 수 있다. 모양이 맞는 항목만 살린다.
    return parsed.filter(isSlot);
  } catch {
    // 사생활 보호 모드에서 접근이 막히거나 JSON 이 깨졌을 뿐이다. 기본 배치로 뜨면 된다.
    return null;
  }
}

function writeLocalLayout(userId: string, slots: WidgetSlot[]): void {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(slots));
  } catch {
    // 저장을 못 해도 이번 세션의 화면은 바뀐 대로 유지된다.
  }
}

function dismissedKey(userId: string): string {
  return `${DISMISSED_PREFIX}.${userId}`;
}

function readDismissed(userId: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(dismissedKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return new Set(Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : []);
  } catch {
    // 못 읽으면 「닫은 적 없음」 이다 — 알림이 뜨는 쪽이 안전하다.
    return new Set();
  }
}

function writeDismissed(userId: string, keys: Set<string>): void {
  try {
    window.localStorage.setItem(dismissedKey(userId), JSON.stringify([...keys]));
  } catch {
    // 못 적으면 다음에 다시 뜬다. 알림이 한 번 더 뜨는 쪽이 안 뜨는 것보다 낫다.
  }
}

function removeLocalLayout(userId: string): void {
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    // 위와 같다.
  }
}

function isSlot(value: unknown): value is WidgetSlot {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.widgetKey === 'string' &&
    (candidate.size === 'sm' || candidate.size === 'wide') &&
    typeof candidate.visible === 'boolean' &&
    // 배율 배치는 없을 수 있다(안 쓰는 사람). 다만 <b>있는데 숫자가 아니면</b> 버린다 —
    // 그대로 두면 격자 폭 계산에 NaN 이 섞여 그 위젯이 화면에서 사라진다.
    isPercent(candidate.widthPercent) &&
    isPercent(candidate.heightPercent) &&
    isSeat(candidate.col) &&
    isSeat(candidate.row) &&
    (candidate.pinned === undefined || candidate.pinned === null || typeof candidate.pinned === 'boolean')
  );
}

/** 좌표 한 칸. 0 도 제대로 된 값이다 — 왼쪽 맨 위가 (0, 0) 이다. */
function isSeat(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/**
 * 배율 한 칸의 값이 쓸 만한가.
 *
 * <b>`null` 을 반드시 통과시켜야 한다.</b> 서버는 배율을 안 쓰는 줄을 `null` 로 내려보내는데
 * (`HomeWidgetSlotResponse`), 여기서 걸러 버리면 <b>그 줄 전체가 버려져</b> 위젯이 홈에서 사라진다 —
 * 배율을 한 번도 안 쓴 사람의 홈이 통째로 비는 길이다.
 */
function isPercent(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
